import type { KostnadskjemaSummary } from '@klp/prosjekt/services/types';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

export const useKostnadsForm = () => {
    const { t } = useTranslation();
    const isNewKostnadskjema = useRef(true);
    const lastSubmittedRef = useRef<KostnadskjemaSummary | undefined>(
        undefined,
    );

    const initializeKostnadskjema = (
        prosessInstansId: string,
        prosjektId: string,
        kostnadskjema: KostnadskjemaSummary,
    ): KostnadskjemaSummary => {
        lastSubmittedRef.current = kostnadskjema;

        return {
            ...kostnadskjema,
            prosessInstansId,
            prosjektId,
        };
    };

    const satsSchema = Yup.number()
        .required(t('prosjekt.kostnadskalkyle.error.satsRequired'))
        .min(0, t('prosjekt.kostnadskalkyle.error.satsRange'))
        .max(100, t('prosjekt.kostnadskalkyle.error.satsRange'));

    const validationSchema = Yup.object().shape({
        risikotilleggSats: satsSchema,
        prosjektlederSats: satsSchema,
        kostnadslinjer: Yup.array()
            .required()
            .of(
                Yup.object().shape({
                    kostnadstype: Yup.object().required(
                        t(
                            'prosjekt.kostnadskalkyle.error.kostnadstypeRequired',
                        ),
                    ),
                    antallEnhet: Yup.number()
                        .required(
                            t('prosjekt.kostnadskalkyle.error.enhetRequired'),
                        )
                        .positive(
                            t('prosjekt.kostnadskalkyle.error.enhetPositive'),
                        ),
                    prisPrEnhet: Yup.number()
                        .required(
                            t(
                                'prosjekt.kostnadskalkyle.error.prisPerEnhetRequired',
                            ),
                        )
                        .positive(
                            t(
                                'prosjekt.kostnadskalkyle.error.prisPerEnhetPositive',
                            ),
                        ),
                    andelKostnadsforing: Yup.number()
                        .required(
                            t(
                                'prosjekt.kostnadskalkyle.error.andelKostnadsforingRequired',
                            ),
                        )
                        .min(0),
                }),
            ),
    });

    return {
        initializeKostnadskjema,
        validationSchema,
        isNewKostnadskjema: isNewKostnadskjema.current,
    };
};
